import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useProfile } from '@/hooks/useProfile';
import { DemoLogin } from '@/pages/auth/DemoLogin';
import ChatPage from '@/pages/chat/ChatPage';
import ChatWrapper from '@/pages/chat/components/ChatWrapper';
import LoginCallbackPage from '@/pages/LoginCallbackPage';
import PermissionErrorPage from '@/pages/PermissionErroPage';
import ResourceCenter from '@/pages/resource_center/resource_center';
import Resources from '@/pages/resources/resources';
import StaffPage from '@/pages/staff/StaffPage';
import { usePermissions } from '@/providers/userContextProvider';
import { Box, CircularProgress } from '@mui/material';
import { useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import ErrorBoundaryComponent from './errorBoundary';
import Layout from './layout';
import { useMenuSections } from './sideBar';
import ToolkitPage from '@/pages/ToolkitPage';

// Create a wrapper component that handles the navigation
const RouterWrapper = () => {
  const isStaff = process.env.REACT_APP_ENV === 'staff';
  const { profileStatus, profile } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (profileStatus === 'COMPLETED' && isStaff) {
      const atRoot = location.pathname === '/';
      const notOnStaffPage = !location.pathname.includes('/staff');
      const missingUuid = !profile?.value?.[0]?.eXpMember?.Uuid;

      if (atRoot || (notOnStaffPage && missingUuid)) {
        navigate('/staff');
      }
    }
  }, [profileStatus, location.pathname, navigate, isStaff, profile]);

  return <Outlet />;
};

const FullPageLoader = () => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100dvh',
      }}
    >
      <CircularProgress
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />
    </Box>
  );
};

export const AppRouter = ({ isAuthRoute }: { isAuthRoute?: boolean }) => {
  const isStaff = process.env.REACT_APP_ENV === 'staff';
  const { accessiblePages } = usePermissions();
  const { canUseFeature } = useFeatureFlags();

  const authRoutesArray = createRoutesFromElements(
    <>
      <Route path="auth.html" element={<LoginCallbackPage />} />
      <Route path="demo/login" element={<DemoLogin />} />
      <Route path="*" element={<FullPageLoader />} />
    </>,
  );

  const routesArray = createRoutesFromElements(
    <>
      <Route element={<RouterWrapper />}>
        {/* Conditionally allow /staff route */}
        <Route
          path="/staff"
          element={isStaff ? <StaffPage /> : <PermissionErrorPage />}
        />

        {canUseFeature(FEATURE_FLAGS.ENABLE_AGENT_PLATFORM) ? (
          accesibleRoutes(accessiblePages)
        ) : (
          <Route path="*" element={<PermissionErrorPage />} />
        )}
      </Route>
    </>,
  );

  const routes = isAuthRoute ? authRoutesArray : routesArray;
  const router = createBrowserRouter(routes);

  return <RouterProvider router={router} />;
};

const accesibleRoutes = (accessiblePages: string[]) => (
  <Route
    path="/"
    element={<Layout />}
    errorElement={<ErrorBoundaryComponent />}
  >
    <Route index element={<FallbackRoute />} />

    {accessiblePages.includes('NEW_CHAT') && (
      <Route path="chat" element={<ChatPage chatId="1234" />} />
    )}

    {accessiblePages.includes('CHAT') && (
      <Route path="/chat">
        <Route index element={<ChatWrapper />} />
        <Route path=":chatId" element={<ChatWrapper />} />
      </Route>
    )}

    {accessiblePages.includes('RESOURCES') && (
      <Route path="resources" element={<Resources />} />
    )}

    {/* {accessiblePages.includes('RESOURCE_CENTER') && (
      <Route path="resourceCenter" element={<ResourceCenter />} />
    )} */}
    {accessiblePages.includes('EXPLORE') && (
      <Route path="/explore">
        {/* <Route
          path="performance"
          element={<ChatPage type="Performance" chatId="" />}
        />
        <Route
          path="transactions"
          element={<ChatPage type="Transactions" chatId="" />}
        />
        <Route
          path="marketing"
          element={<ChatPage type="Marketing" chatId="" />}
        /> */}
        <Route path="support" element={<ResourceCenter />} />
        <Route path="toolkit" element={<ToolkitPage />} />
      </Route>
    )}

    <Route path="*" element={<FallbackRoute />} />
  </Route>
);

/* Access to the non-accessible or non-existent URI */
const FallbackRoute = () => {
  const { accessiblePages } = usePermissions();
  const navigate = useNavigate();
  const pageIsAccessible = (key: string): boolean =>
    accessiblePages.includes(key);

  /* Redirects the browser to the first accessible page */
  const allMenuItems = useMenuSections()
    .map((sect) => sect.subSections)
    .flat();
  const accessibleMenuItems = allMenuItems.filter((item) =>
    pageIsAccessible(item.accessKey),
  );
  const firstAccessiblePath = accessibleMenuItems[0]?.path;
  useEffect(() => {
    if (firstAccessiblePath) {
      navigate(firstAccessiblePath);
    }
  }, [navigate, firstAccessiblePath]);

  return <></>;
};
